import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { Logo } from 'src/components/Logo';
import { SEO } from 'src/components/SEO';
import { PublicMarketingFooter } from 'src/components/PublicMarketingFooter';
import {
  ConnectorMarquee,
  GrainOverlay,
  HeroMesh,
  HeroStagger,
  HeroStaggerItem,
  MotionCta,
  RevealOnScroll,
} from './homeAtmosphere';
import { HomeStackExplorer, type StackStory } from './homeStackExplorer';
import { HomeMcpSection } from './homeMcpSection';

// Styled components per la home page
const HomeContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(1000px 520px at 8% -8%, rgba(30, 237, 159, 0.1), transparent 62%), radial-gradient(900px 480px at 96% 8%, rgba(56, 189, 248, 0.06), transparent 58%), #06090e',
  color: 'white',
  fontFamily: "'Space Grotesk', sans-serif",
  margin: 0,
  padding: 0,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'auto',
  scrollBehavior: 'smooth',
  '@keyframes softFloat': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-8px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '*': {
      animation: 'none !important',
      transition: 'none !important',
    },
  },
}));

const Navbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '30px 10%',
  backgroundColor: 'rgba(7, 11, 17, 0.72)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  backdropFilter: 'blur(14px)',
  width: '100%',
  boxSizing: 'border-box',
  position: 'sticky',
  top: 0,
  zIndex: 1000,

  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 10%',
    position: 'relative',
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.6rem',
  fontWeight: 600,
  color: 'rgb(30, 237, 159)',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '30px',

  [theme.breakpoints.down('md')]: {
    display: 'none', // Nascondi i link normali su mobile
  },
}));

// Styled components per il menu mobile stile app
const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: 'white',

  [theme.breakpoints.down('md')]: {
    display: 'block',
    position: 'absolute',
    right: '10%',
  },
}));

const MobileDrawer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  width: '280px',
  height: '100vh',
  backgroundColor: '#0b0e13',
  color: 'white',
  zIndex: 1300,
  transform: 'translateX(100%)',
  transition: 'transform 0.3s ease-in-out',
  padding: '20px',
  boxSizing: 'border-box',
  display: 'none', // Nascondi di default

  [theme.breakpoints.down('md')]: {
    display: 'block', // Mostra solo su mobile
  },

  '&.open': {
    transform: 'translateX(0)',
  },
}));

const MobileDrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginBottom: '30px',
  paddingBottom: '20px',
  borderBottom: '1px solid #333',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  padding: '8px',

  '&:hover': {
    color: 'rgb(30, 237, 159)',
    backgroundColor: 'rgba(30, 237, 159, 0.1)',
  },
}));

const MobileNavItem = styled(Box)(({ theme }) => ({
  padding: '15px 0',
  cursor: 'pointer',
  borderBottom: '1px solid #333',
  fontSize: '1.1rem',
  color: '#d2d2d2',
  transition: 'color 0.2s ease',

  '&:hover': {
    color: 'rgb(30, 237, 159)',
  },

  '&:last-child': {
    borderBottom: 'none',
  },
}));

const MobileNavItemLogin = styled(Box)(({ theme }) => ({
  padding: '15px 0',
  cursor: 'pointer',
  borderBottom: '1px solid #333',
  fontSize: '1.1rem',
  color: 'rgb(30, 237, 159)',
  transition: 'color 0.2s ease',

  '&:hover': {
    color: 'rgb(30, 237, 159)',
    backgroundColor: 'rgba(30, 237, 159, 0.1)',
  },

  '&:last-child': {
    borderBottom: 'none',
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1299,
  display: 'none',

  '&.open': {
    display: 'block',
  },
}));

const NavLink = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  textDecoration: 'none',
  cursor: 'pointer',
  fontSize: '0.95rem',
  fontWeight: 500,
  position: 'relative',
  transition: 'color 0.2s ease, transform 0.2s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: '-6px',
    width: '100%',
    height: '2px',
    borderRadius: '999px',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    backgroundColor: 'rgba(30, 237, 159, 0.95)',
    transition: 'transform 0.22s ease',
  },
  '&:hover': {
    color: 'rgb(30, 237, 159)',
    transform: 'translateY(-1px)',
  },
  '&:hover::after': {
    transform: 'scaleX(1)',
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgb(30, 237, 159)',
  color: '#000',
  fontWeight: 700,
  padding: '9px 18px',
  borderRadius: '999px',
  fontSize: '0.9rem',
  textTransform: 'none',
  minWidth: 'auto',
  height: '36px',
  boxShadow: '0 8px 22px rgba(30, 237, 159, 0.28)',
  '&:hover': {
    backgroundColor: 'rgb(25, 200, 130)',
    boxShadow: '0 10px 24px rgba(30, 237, 159, 0.36)',
  },
  transition: 'all 0.2s ease',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '96px 10% 88px',
  background:
    'linear-gradient(180deg, rgba(9, 13, 19, 0.55) 0%, rgba(7, 11, 17, 0.92) 100%)',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'center',
    padding: '56px 5% 48px',
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3.2rem',
  fontWeight: 700,
  lineHeight: 1.12,
  marginBottom: '24px',
  letterSpacing: '-0.02em',

  [theme.breakpoints.down('md')]: {
    fontSize: 'clamp(1.65rem, 7.2vw, 2.1rem)',
    marginBottom: '16px',
    lineHeight: 1.15,
  },
})) as typeof Typography;

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  color: 'rgba(230, 234, 240, 0.8)',
  fontSize: '1.05rem',
  marginBottom: '30px',
  lineHeight: 1.75,
  maxWidth: '52ch',

  [theme.breakpoints.down('md')]: {
    fontSize: '0.95rem',
    lineHeight: 1.65,
    marginBottom: '22px',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '36ch',
  },
}));

const CTAButton = MotionCta;

const HeroImage = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100%',
  animation: 'softFloat 7.2s ease-in-out 1.1s infinite',

  [theme.breakpoints.down('md')]: {
    animation: 'none',
    paddingTop: '4px',
  },
}));

const HeroImg = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '520px',
  height: 'auto',
  display: 'block',
  margin: '0 auto',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  boxShadow:
    '0 26px 60px rgba(0, 0, 0, 0.48), 0 0 40px rgba(30, 237, 159, 0.16)',

  [theme.breakpoints.down('md')]: {
    maxWidth: 'min(100%, 380px)',
    borderRadius: '14px',
  },
}));

const ConnectorStrip = styled(Box)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '28px 10% 40px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  background:
    'linear-gradient(180deg, rgba(7, 11, 17, 0.98) 0%, rgba(8, 13, 20, 1) 100%)',
  position: 'relative',
  zIndex: 1,

  [theme.breakpoints.down('md')]: {
    padding: '20px 8% 32px',
  },
}));

const ConnectorStripLabel = styled(Typography)({
  color: 'rgba(210, 218, 228, 0.55)',
  fontSize: '0.78rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: '16px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: 'rgb(30, 237, 159)',
  },
});

const ConnectorChip = styled('button')({
  flexShrink: 0,
  padding: '8px 14px',
  borderRadius: '999px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'rgba(14, 20, 30, 0.7)',
  color: 'rgba(230, 234, 240, 0.88)',
  fontSize: '0.84rem',
  fontWeight: 500,
  fontFamily: 'inherit',
  letterSpacing: '0.01em',
  lineHeight: 1.3,
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(30, 237, 159, 0.45)',
    color: 'rgb(30, 237, 159)',
    transform: 'translateY(-1px)',
  },
  '&:focus-visible': {
    outline: '2px solid rgb(30, 237, 159)',
    outlineOffset: '2px',
  },
});

const MARKETING_CONNECTORS = [
  'Shopify',
  'Magento',
  'Google Ads',
  'Meta Ads',
  'GA4',
  'Search Console',
  'Database',
  'Kipify MCP',
] as const;

const STACK_STORIES: StackStory[] = [
  {
    id: 'stack-ads',
    tab: 'Paid media',
    viz: 'paid',
    question: 'Meta reports a strong ROAS. Why aren’t total sales growing?',
    headline: 'See ad spend alongside real store results.',
    points: [
      'Combine Google and Meta spend with actual store revenue.',
      'Track blended MER and revenue trends beyond platform attribution.',
      'Spot when higher ad spend isn’t translating into more sales.',
    ],
    whyItMatters:
      'Compare platform-reported performance with business-level outcomes.',
  },
  {
    id: 'stack-organic',
    tab: 'Organic search',
    viz: 'organic',
    question: 'Which search queries are we missing opportunities on?',
    headline: 'Turn Search Console data into SEO opportunities.',
    points: [
      'Find high-impression queries with underperforming CTR.',
      'Identify pages ranking just outside the top results.',
      'Compare organic visibility trends with paid media and store performance.',
    ],
    whyItMatters:
      'Prioritize queries and pages using measurable search demand.',
  },
  {
    id: 'stack-ga4',
    tab: 'Website analytics',
    viz: 'analytics',
    question: 'Traffic is growing. Why aren’t orders keeping up?',
    headline: 'Put traffic and engagement in the context of sales.',
    points: [
      'Compare sessions, engagement and orders over the same period.',
      'Find channels where traffic growth outpaces conversions.',
      'Investigate reporting differences between GA4 and your store.',
    ],
    whyItMatters:
      'Understand where traffic growth isn’t becoming business growth.',
  },
  {
    id: 'stack-store',
    tab: 'Store performance',
    viz: 'store',
    question: 'Sales are up. Are we earning more from them?',
    headline: 'Look beyond revenue to product performance and costs.',
    points: [
      'Explore sales by product, country and customer type.',
      'Review discounts, refunds and available product cost data.',
      'Identify what drives revenue versus estimated contribution margin, when costs are configured.',
    ],
    whyItMatters: 'Go beyond best sellers to understand their economics.',
  },
];

const ScrollToTopButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  backgroundColor: 'rgb(30, 237, 159)',
  color: '#000',
  minWidth: '50px',
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  boxShadow: '0 4px 12px rgba(30, 237, 159, 0.4)',
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid rgba(255, 255, 255, 0.35)',
  '&:hover': {
    backgroundColor: 'rgb(25, 200, 130)',
    boxShadow: '0 6px 16px rgba(30, 237, 159, 0.6)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

// FAQ Styled Components
const FAQAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'rgba(9, 14, 22, 0.9)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '12px !important',
  marginBottom: '16px',
  boxShadow: '0 10px 26px rgba(0, 0, 0, 0.22)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 16px 0',
    backgroundColor: 'rgba(14, 24, 34, 0.96)',
    borderColor: 'rgba(30, 237, 159, 0.7)',
  },
  '&:focus': {
    outline: 'none',
  },
  '&.Mui-focusVisible': {
    outline: 'none',
  },
}));

const FAQSummary = styled(AccordionSummary)(({ theme }) => ({
  color: 'white',
  fontSize: '1.05rem',
  fontWeight: 600,
  padding: '18px 22px',
  '& .MuiAccordionSummary-content': {
    margin: '0',
  },
  '&.Mui-expanded': {
    color: 'rgb(30, 237, 159)',
  },
  '&:focus': {
    outline: 'none',
  },
  '&.Mui-focusVisible': {
    outline: 'none',
  },
}));

const FAQDetails = styled(AccordionDetails)(({ theme }) => ({
  color: 'rgba(220, 226, 235, 0.84)',
  fontSize: '1rem',
  lineHeight: 1.6,
  padding: '0 22px 22px 22px',
}));

const FAQExpandIcon = styled(ExpandMoreIcon)(({ theme }) => ({
  color: 'rgb(30, 237, 159)',
  fontSize: '1.5rem',
}));

// FAQ Component
const FAQComponent: React.FC = () => {
  const faqs = [
    {
      question: 'How much time does it take to set up Kipify?',
      answer:
        'Kipify connects to your existing platforms in under 5 minutes. Authenticate Shopify or Magento, Google Ads, Meta Ads, GA4, and Google Search Console with secure OAuth—and optionally add a database or Kipify MCP for Claude and ChatGPT. Your data starts syncing automatically. No complex setup required.',
    },
    {
      question: 'Do I need to export data manually from different platforms?',
      answer:
        'Never again! Kipify automatically syncs your data from all connected platforms in real-time. No more CSV exports, manual downloads, or data consolidation. Your data is always up-to-date and accessible through our intuitive interface.',
    },
    {
      question: 'Can I ask questions about my data in plain language?',
      answer:
        "Absolutely! Kipify's AI understands natural language queries in any language. Ask questions like 'What's my best-selling product this month?' or 'Show me Meta ad performance vs Google Ads and Search Console clicks' and get instant, accurate answers from your connected data—in the product or via Kipify MCP in Claude and ChatGPT.",
    },
    {
      question:
        "What if I need custom metrics that aren't available in standard reports?",
      answer:
        "Kipify's Custom Formulas let you create your own business metrics by combining data from multiple platforms. Build unique KPIs like 'Customer Lifetime Value by Traffic Source' or 'ROI per Marketing Channel' using simple mathematical operations.",
    },
    {
      question: 'Why choose Kipify over other analytics platforms?',
      answer:
        'Most analytics platforms require technical setup, force you into rigid dashboards, and need experts to build your reports. Kipify works in minutes with natural language queries - no technical skills needed. Ask any question about your data and get instant answers, or create custom metrics that actually matter to YOUR business. No waiting for specialists or learning complex interfaces.',
    },
    {
      question:
        'Is connecting Claude or ChatGPT to Meta, Google, and Shopify enough—or do I still need Kipify?',
      answer:
        'Raw MCP connectors can pull live data from each account—but you still juggle different metrics, date ranges, and definitions. Easy answers in chat can miss how one channel looks next to the rest and next to actual revenue. Kipify is the layer that connects and compares that marketing and sales data in one place—with stable, repeatable definitions. Use it in the product, or ask the same questions in Claude or ChatGPT via Kipify MCP, so insight stays consistent instead of being stitched together conversation by conversation.',
    },
  ];

  return (
    <>
      {faqs.map((faq, index) => (
        <FAQAccordion key={index}>
          <FAQSummary expandIcon={<FAQExpandIcon />}>{faq.question}</FAQSummary>
          <FAQDetails>{faq.answer}</FAQDetails>
        </FAQAccordion>
      ))}
    </>
  );
};

export const Home: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const aboutRef = React.useRef<HTMLDivElement | null>(null);
  const faqRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [visibleSections, setVisibleSections] = useState({
    about: false,
    faq: false,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Gestisci parametri Shopify
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const shop = searchParams.get('shop');
    const hmac = searchParams.get('hmac');
    const host = searchParams.get('host');
    const session = searchParams.get('session');
    const timestamp = searchParams.get('timestamp');

    // Persisti shop se presente per usarlo dopo il login
    try {
      if (shop) {
        sessionStorage.setItem('shopify_shop', shop);
      }
    } catch {}

    // Se c'è un code Shopify, redirecta al callback (OAuth flow)
    if (code && shop) {
      const shopifyCallbackUrl = `/${ROUTE_SOURCES.SHOPIFY_AUTH_CALLBACK}?${searchParams.toString()}`;
      navigate(shopifyCallbackUrl, { replace: true });
      return;
    }

    // Se arriviamo dallo store (parametri Shopify completi)
    if (hmac && host && session && shop && timestamp) {
      navigate(`/${ROUTE_SOURCES.LOGIN}?${searchParams.toString()}`, {
        replace: true,
      });
      return;
    }
  }, [location.search, navigate]);

  // Aggiungi il font Google Fonts
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Override specifico per HomeContainer height
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '.css-16cdv7m { height: 100% !important; }';
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Gestisci scroll to top button (listener sul contenitore della pagina)
  useEffect(() => {
    const container = containerRef.current;

    const getScrollTop = () => {
      if (containerRef.current) return containerRef.current.scrollTop;
      return window.pageYOffset || document.documentElement.scrollTop;
    };

    const handleScroll = () => {
      const scrollTop = getScrollTop();
      setShowScrollToTop(scrollTop > 300);
    };

    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Trigger iniziale
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }

    // Fallback su window (in caso di ref non disponibile)
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const options: IntersectionObserverInit = {
      threshold: 0.18,
      rootMargin: '0px 0px -8% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const sectionId = entry.target.getAttribute('data-reveal-section');
        if (!sectionId) return;

        setVisibleSections((prev) => ({ ...prev, [sectionId]: true }));
        observer.unobserve(entry.target);
      });
    }, options);

    const targets = [aboutRef.current, faqRef.current];
    targets.forEach((target) => {
      if (target) observer.observe(target);
    });

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    navigate(`/${ROUTE_SOURCES.LOGIN}`);
  };

  const handleScrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false); // Chiudi il menu dopo il click
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleScrollToTop = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Structured Data per SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://app.kipify.io/#organization',
        name: 'Kipify',
        url: 'https://app.kipify.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://app.kipify.io/images/hero-header-kipify.png',
        },
        sameAs: ['https://www.linkedin.com/company/kipify'],
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'hr@kipify.io',
          contactType: 'Customer Service',
        },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://app.kipify.io/#software',
        name: 'Kipify',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
          description: '14-day free trial, no credit card required',
        },
        description:
          'Analytics Platform per E-commerce. Connetti Shopify, Magento, Google Ads, Meta Ads, GA4 e Search Console. Stesse metriche in dashboard o via Kipify MCP in Claude e ChatGPT. ROAS, ACOS, margini e revenue con KPI personalizzati.',
      },
      {
        '@type': 'WebSite',
        '@id': 'https://app.kipify.io/#website',
        url: 'https://app.kipify.io',
        name: 'Kipify',
        publisher: {
          '@id': 'https://app.kipify.io/#organization',
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://app.kipify.io/?s={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return (
    <HomeContainer ref={containerRef}>
      <GrainOverlay />
      <SEO
        title="Kipify - Analytics E-commerce | MCP, Search Console, Dashboard"
        description="Connetti Shopify, Magento, Google Ads, Meta Ads, GA4 e Search Console. Stesse metriche in Kipify o via MCP in Claude e ChatGPT. Prova gratis 14 giorni."
        canonical="https://app.kipify.io"
        ogImage="https://app.kipify.io/images/hero-header-kipify.png"
        structuredData={structuredData}
      />
      {/* Navbar */}
      <Navbar>
        <LogoContainer>
          <Logo variant="default" size="medium" />
          <LogoText>Kipify</LogoText>
        </LogoContainer>
        {!isMobile && (
          <NavLinks>
            <NavLink onClick={() => handleScrollToSection('about')}>
              Why Kipify
            </NavLink>
            <NavLink onClick={() => handleScrollToSection('mcp')}>MCP</NavLink>
            <NavLink onClick={() => handleScrollToSection('stack')}>
              Stack
            </NavLink>
            <NavLink onClick={() => handleScrollToSection('how-it-works')}>
              Product
            </NavLink>
            <NavLink onClick={() => navigate(`/${ROUTE_SOURCES.BLOG}`)}>
              Blog
            </NavLink>
            <LoginButton onClick={handleGetStarted}>Start free</LoginButton>
          </NavLinks>
        )}
        <MobileMenuButton onClick={handleMobileMenuToggle}>
          <MenuIcon />
        </MobileMenuButton>
      </Navbar>

      {/* Hero Section */}
      <HeroSection>
        <HeroMesh />
        <HeroStagger
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: '28px', md: '24px' },
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <HeroStaggerItem
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              width: '100%',
              minWidth: 0,
              maxWidth: { xs: '100%', md: '540px' },
              textAlign: { xs: 'center', md: 'left' },
              boxSizing: 'border-box',
            }}
          >
            <Typography
              sx={{
                color: 'rgb(30, 237, 159)',
                fontSize: { xs: '0.7rem', md: '0.78rem' },
                fontWeight: 700,
                letterSpacing: { xs: '0.1em', md: '0.14em' },
                textTransform: 'uppercase',
                marginBottom: '14px',
              }}
            >
              Ecommerce analytics + AI
            </Typography>
            <HeroTitle component="h1" variant="h1">
              <Box component="span" sx={{ color: 'white', display: 'block' }}>
                See beyond ROAS.
              </Box>
              <Box
                component="span"
                sx={{ color: 'rgb(30, 237, 159)', display: 'block' }}
              >
                Understand what drives your business.
              </Box>
            </HeroTitle>
            <HeroSubtitle>
              Connect your store, ads and analytics in one place. Track sales,
              marketing performance, margins and inventory—then explore your
              data in Kipify, ChatGPT or Claude.
            </HeroSubtitle>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: '12px', sm: '16px' },
                justifyContent: { xs: 'center', md: 'flex-start' },
                marginBottom: '18px',
                width: '100%',
              }}
            >
              <CTAButton
                onClick={handleGetStarted}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '320px', sm: 'none' },
                }}
              >
                Start free trial
              </CTAButton>
              <Typography
                component="button"
                onClick={() => handleScrollToSection('how-it-works')}
                sx={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'rgba(230, 234, 240, 0.75)',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit',
                  textDecoration: 'underline',
                  textUnderlineOffset: '4px',
                  textDecorationColor: 'rgba(255,255,255,0.25)',
                  '&:hover': { color: 'rgb(30, 237, 159)' },
                }}
              >
                See Kipify in action
              </Typography>
            </Box>
            <Typography
              sx={{
                color: 'rgba(210, 218, 228, 0.5)',
                fontSize: { xs: '0.72rem', md: '0.8rem' },
                letterSpacing: '0.01em',
                lineHeight: 1.55,
                maxWidth: { xs: '28ch', md: 'none' },
                mx: { xs: 'auto', md: 0 },
              }}
            >
              Shopify · Meta Ads · Google Ads · GA4 · Search Console · MCP
            </Typography>
          </HeroStaggerItem>
          <HeroStaggerItem
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              width: '100%',
              minWidth: 0,
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            <HeroImage>
              <HeroImg src="/images/hero-header-kipify.png" alt="Kipify Hero" />
            </HeroImage>
          </HeroStaggerItem>
        </HeroStagger>
      </HeroSection>

      <ConnectorStrip>
        <ConnectorStripLabel
          onClick={handleGetStarted}
          role="link"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleGetStarted();
            }
          }}
        >
          Connect your stack
        </ConnectorStripLabel>
        <ConnectorMarquee
          items={MARKETING_CONNECTORS}
          renderItem={(name, key) => {
            const sectionId =
              name === 'Google Ads' || name === 'Meta Ads'
                ? 'stack-ads'
                : name === 'GA4'
                  ? 'stack-ga4'
                  : name === 'Search Console'
                    ? 'stack-organic'
                    : name === 'Shopify' ||
                        name === 'Magento' ||
                        name === 'Database'
                      ? 'stack-store'
                      : name === 'Kipify MCP'
                        ? 'mcp'
                        : null;

            return (
              <ConnectorChip
                key={key}
                type="button"
                aria-label={`Learn how Kipify helps with ${name}`}
                onClick={() => {
                  if (sectionId === 'mcp') {
                    handleScrollToSection('mcp');
                    return;
                  }
                  if (sectionId) {
                    window.location.hash = sectionId;
                    handleScrollToSection('stack');
                    return;
                  }
                  handleGetStarted();
                }}
              >
                {name}
              </ConnectorChip>
            );
          }}
        />
      </ConnectorStrip>

      {/* Why Kipify — conversion outcomes */}
      <RevealOnScroll
        id="about"
        sx={{
          padding: { xs: '72px 8%', md: '96px 10%' },
          background:
            'linear-gradient(180deg, rgba(8, 13, 20, 0.96) 0%, rgba(7, 11, 18, 0.98) 100%)',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box ref={aboutRef} data-reveal-section="about">
          <Box
            sx={{ maxWidth: '920px', margin: '0 auto', textAlign: 'center' }}
          >
            <Typography
              sx={{
                color: 'rgb(30, 237, 159)',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                marginBottom: '14px',
              }}
            >
              Why Kipify
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.12,
                marginBottom: '16px',
              }}
            >
              Know what actually makes money
            </Typography>
            <Typography
              sx={{
                color: 'rgba(210, 218, 228, 0.8)',
                fontSize: '1.15rem',
                lineHeight: 1.7,
                maxWidth: '52ch',
                margin: '0 auto 48px',
              }}
            >
              Ads, Search Console, GA4, and your store finally tell the same
              story—so you scale profit, not vanity metrics.
            </Typography>
          </Box>

          <Box
            sx={{
              maxWidth: '1000px',
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: '28px', md: '40px' },
            }}
          >
            {[
              {
                title: 'Cut wasted ad spend',
                body: 'See Google and Meta next to real sales—not platform ROAS that never matches the bank.',
              },
              {
                title: 'Decide with one number',
                body: 'Paid, organic, analytics, and ecommerce share the same definitions. Fewer debates. Faster moves.',
              },
              {
                title: 'Ask in plain language',
                body: 'Get answers in Kipify—or in Claude and ChatGPT via Kipify MCP—without rebuilding the spreadsheet.',
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{ textAlign: { xs: 'center', md: 'left' } }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 2,
                    backgroundColor: 'rgb(30, 237, 159)',
                    marginBottom: '16px',
                    mx: { xs: 'auto', md: 0 },
                  }}
                />
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    marginBottom: '10px',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(210, 218, 228, 0.72)',
                    fontSize: '0.98rem',
                    lineHeight: 1.65,
                  }}
                >
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center', marginTop: '48px' }}>
            <CTAButton onClick={handleGetStarted}>
              Start free — 14 days
            </CTAButton>
            <Typography
              sx={{
                marginTop: '14px',
                color: 'rgba(210, 218, 228, 0.55)',
                fontSize: '0.85rem',
              }}
            >
              No credit card · Connect in minutes · Cancel anytime
            </Typography>
          </Box>
        </Box>
      </RevealOnScroll>

      <HomeMcpSection
        onCta={handleGetStarted}
        onSeeHow={() => handleScrollToSection('how-it-works')}
      />

      {/* Simple 3 steps */}
      <RevealOnScroll
        id="steps"
        sx={{
          padding: { xs: '64px 8%', md: '80px 10%' },
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          background: '#070b11',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'white',
            fontSize: { xs: '1.75rem', md: '2.2rem' },
            fontWeight: 700,
            textAlign: 'center',
            letterSpacing: '-0.02em',
            marginBottom: '40px',
          }}
        >
          Live in three steps
        </Typography>
        <Box
          sx={{
            maxWidth: '960px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: '24px',
          }}
        >
          {[
            {
              step: '01',
              title: 'Connect',
              body: 'Shopify or Magento, ads, GA4, Search Console—secure OAuth, no CSV ritual.',
            },
            {
              step: '02',
              title: 'See profit clearly',
              body: 'One dashboard where spend, traffic, and margin finally line up.',
            },
            {
              step: '03',
              title: 'Ask anything',
              body: 'In Kipify or via MCP in Claude / ChatGPT—same trusted answers.',
            },
          ].map((item) => (
            <Box key={item.step} sx={{ textAlign: 'center', px: 1 }}>
              <Typography
                sx={{
                  color: 'rgb(30, 237, 159)',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  marginBottom: '12px',
                }}
              >
                {item.step}
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  marginBottom: '8px',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(210, 218, 228, 0.7)',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                }}
              >
                {item.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </RevealOnScroll>

      <RevealOnScroll>
        <HomeStackExplorer stories={STACK_STORIES} onCta={handleGetStarted} />
      </RevealOnScroll>

      {/* Product proof — 3 beats only (KPI, connect, ask) */}
      <Box
        id="how-it-works"
        sx={{
          padding: { xs: '64px 6%', md: '88px 10%' },
          background:
            'linear-gradient(180deg, rgba(8, 13, 20, 1) 0%, rgba(7, 12, 18, 1) 100%)',
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'white',
            fontSize: { xs: '2rem', md: '2.6rem' },
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '14px',
            letterSpacing: '-0.02em',
          }}
        >
          See the product
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(210, 218, 228, 0.78)',
            fontSize: '1.1rem',
            textAlign: 'center',
            maxWidth: '48ch',
            margin: '0 auto 56px auto',
            lineHeight: 1.65,
          }}
        >
          KPIs, connectors, ask, your formulas, inventory—same truth in the
          product and via MCP.
        </Typography>

        {(
          [
            {
              img: '/images/dashboard-kpi.png',
              alt: 'Profit-first KPI dashboard',
              title: 'Profit-first KPIs',
              body: 'ROAS, ACOS, margin, and revenue in one place—period comparisons that match the bank, not just the ad platforms.',
              chips: ['ROAS & ACOS', 'Margin', 'Period compare'],
              reverse: false,
            },
            {
              img: '/images/dashboard-connectors.png',
              alt: 'Connected data sources',
              title: 'One stack, connected once',
              body: 'Shopify or Magento, Google Ads, Meta, GA4, Search Console—secure OAuth, then the same definitions via Kipify MCP.',
              chips: ['OAuth', 'Search Console', 'Kipify MCP'],
              reverse: true,
            },
            {
              img: '/images/dashboard-ai.png',
              alt: 'Ask questions in Kipify',
              title: 'Ask in Kipify too',
              body: 'Natural-language answers on the same metrics you use in Claude or ChatGPT—no spreadsheet rebuild.',
              chips: ['Plain language', 'Cross-channel', 'Live data'],
              reverse: false,
            },
            {
              img: '/images/dashboard-formulas.png',
              alt: 'Custom formulas and KPIs',
              title: 'Your formulas, your KPIs',
              body: 'Build the metrics your business actually runs on—combine channels with the math you already trust, not generic templates.',
              chips: ['Custom formulas', 'Cross-platform', 'Reusable KPIs'],
              reverse: true,
            },
            {
              img: '/images/dashboard-inventory.png',
              alt: 'Inventory and sell-through',
              title: 'Inventory that matches demand',
              body: 'Stock, velocity, and sell-out risk next to channel performance—so you don’t scale ads into empty shelves.',
              chips: ['Stock levels', 'Sell-out risk', 'Velocity'],
              reverse: false,
            },
          ] as const
        ).map((block, index) => (
          <Box
            key={block.title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '32px', lg: '64px' },
              marginBottom: index < 4 ? { xs: '64px', md: '88px' } : 0,
              flexDirection: {
                xs: 'column',
                lg: block.reverse ? 'row-reverse' : 'row',
              },
            }}
          >
            <Box
              sx={{
                flex: 1,
                borderRadius: '18px',
                overflow: 'hidden',
                '& img': { transition: 'transform 0.35s ease' },
                '&:hover img': { transform: 'scale(1.018)' },
              }}
            >
              <img
                src={block.img}
                alt={block.alt}
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontSize: { xs: '1.6rem', md: '2rem' },
                  fontWeight: 700,
                  marginBottom: '14px',
                  letterSpacing: '-0.02em',
                }}
              >
                {block.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(210, 218, 228, 0.75)',
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  marginBottom: '22px',
                  maxWidth: '42ch',
                }}
              >
                {block.body}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {block.chips.map((chip) => (
                  <Box
                    key={chip}
                    sx={{
                      border: '1px solid rgba(30, 237, 159, 0.45)',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      color: 'rgb(30, 237, 159)',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                    }}
                  >
                    {chip}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Final conversion CTA */}
      <RevealOnScroll
        id="get-started"
        sx={{
          padding: { xs: '72px 8%', md: '96px 10%' },
          textAlign: 'center',
          background:
            'radial-gradient(640px 280px at 50% 0%, rgba(30, 237, 159, 0.14), transparent 70%), #070b11',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'white',
            fontSize: { xs: '2rem', md: '2.6rem' },
            fontWeight: 700,
            letterSpacing: '-0.03em',
            marginBottom: '14px',
          }}
        >
          Ready to stop guessing?
        </Typography>
        <Typography
          sx={{
            color: 'rgba(210, 218, 228, 0.75)',
            fontSize: '1.1rem',
            maxWidth: '42ch',
            margin: '0 auto 28px',
            lineHeight: 1.65,
          }}
        >
          Start your 14-day free trial. Connect your stack. See what actually
          makes money.
        </Typography>
        <CTAButton onClick={handleGetStarted}>Start free trial</CTAButton>
        <Typography
          sx={{
            marginTop: '14px',
            color: 'rgba(210, 218, 228, 0.5)',
            fontSize: '0.85rem',
          }}
        >
          No credit card required
        </Typography>
      </RevealOnScroll>

      {/* FAQ Section */}
      <Box
        ref={faqRef}
        data-reveal-section="faq"
        sx={{
          padding: '100px 10%',
          backgroundColor: '#0b0e13',
          width: '100%',
          boxSizing: 'border-box',
          opacity: visibleSections.faq ? 1 : 0,
          transform: visibleSections.faq ? 'translateY(0)' : 'translateY(26px)',
          transition: 'opacity 620ms ease, transform 620ms ease',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'rgb(30, 237, 159)',
            fontSize: '3rem',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#d2d2d2',
            fontSize: '1.2rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto 60px auto',
            lineHeight: 1.6,
          }}
        >
          Get answers to the most common questions about Kipify
        </Typography>

        <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
          <FAQComponent />
        </Box>
      </Box>

      {/* Data Usage Transparency Section */}
      <Box
        id="privacy"
        sx={{
          padding: '96px 10%',
          background:
            'linear-gradient(180deg, rgba(7, 12, 18, 1) 0%, rgba(6, 10, 16, 1) 100%)',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'rgb(30, 237, 159)',
            fontSize: '2.5rem',
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Data Usage & Privacy
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#d2d2d2',
            fontSize: '1.1rem',
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          We believe in transparency when it comes to your data. Kipify only
          accesses the data necessary to provide you with valuable business
          insights and analytics.
        </Typography>

        <Grid container spacing={4} sx={{ marginTop: '46px' }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: 'center',
                padding: '26px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                backgroundColor: 'rgba(15, 21, 31, 0.82)',
                boxShadow: '0 14px 30px rgba(0, 0, 0, 0.24)',
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'rgb(30, 237, 159)',
                  fontSize: '1.3rem',
                  marginBottom: '15px',
                }}
              >
                Secure Access
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#d2d2d2',
                  lineHeight: 1.6,
                }}
              >
                We use secure OAuth connections to access only the data you
                authorize, following industry-standard security practices.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: 'center',
                padding: '26px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                backgroundColor: 'rgba(15, 21, 31, 0.82)',
                boxShadow: '0 14px 30px rgba(0, 0, 0, 0.24)',
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'rgb(30, 237, 159)',
                  fontSize: '1.3rem',
                  marginBottom: '15px',
                }}
              >
                Data Purpose
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#d2d2d2',
                  lineHeight: 1.6,
                }}
              >
                Your data is used exclusively to generate insights, create
                reports, and provide personalized recommendations for your
                business growth.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: 'center',
                padding: '26px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                backgroundColor: 'rgba(15, 21, 31, 0.82)',
                boxShadow: '0 14px 30px rgba(0, 0, 0, 0.24)',
                height: '100%',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'rgb(30, 237, 159)',
                  fontSize: '1.3rem',
                  marginBottom: '15px',
                }}
              >
                Your Control
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#d2d2d2',
                  lineHeight: 1.6,
                }}
              >
                You can revoke access to your data at any time through your
                account settings or by disconnecting integrations.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <PublicMarketingFooter />

      {/* Mobile Menu Overlay */}
      <Overlay
        className={mobileMenuOpen ? 'open' : ''}
        onClick={handleMobileMenuClose}
      />

      {/* Mobile Menu Drawer */}
      <MobileDrawer className={mobileMenuOpen ? 'open' : ''}>
        <MobileDrawerHeader>
          <CloseButton onClick={handleMobileMenuClose}>
            <CloseIcon />
          </CloseButton>
        </MobileDrawerHeader>

        <MobileNavItem onClick={() => handleScrollToSection('about')}>
          Why Kipify
        </MobileNavItem>
        <MobileNavItem onClick={() => handleScrollToSection('mcp')}>
          MCP
        </MobileNavItem>
        <MobileNavItem onClick={() => handleScrollToSection('stack')}>
          Stack
        </MobileNavItem>
        <MobileNavItem onClick={() => handleScrollToSection('how-it-works')}>
          Product
        </MobileNavItem>
        <MobileNavItem
          onClick={() => {
            navigate(`/${ROUTE_SOURCES.BLOG}`);
            handleMobileMenuClose();
          }}
        >
          Blog
        </MobileNavItem>
        <MobileNavItemLogin onClick={handleGetStarted}>
          Start free
        </MobileNavItemLogin>
      </MobileDrawer>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <ScrollToTopButton onClick={handleScrollToTop}>
          <KeyboardArrowUpIcon />
        </ScrollToTopButton>
      )}
    </HomeContainer>
  );
};
